import { PrimaryAnnotatedType } from '@/components/annotated-type';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { CenteredStoryText, StoryText } from '@/components/microstory/text';
import { StripPlot } from '@/components/strip-plot';
import { countryIdToName } from '@/utils/country-id-to-name';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { t, Trans } from '@lingui/macro';
import { useState } from 'react';

type FreeToBeYoungChapter1Props = {
  data: FreeToBeYoungData['people_suggested_voting_age_under_legal_voting_age'];
};
export function FreeToBeYoungChapter1(props: FreeToBeYoungChapter1Props) {
  const formatValue = useNumberFormatting('percent');

  const chartData = (Object.entries(props.data) as [CountryIsoCode, number][]).map(
    ([key, value]) => ({
      id: key,
      label: countryIdToName(key),
      value,
    })
  );

  const [highlightedCountries, setHighlightedCountries] = useState<CountryIsoCode[]>();

  const handleStepEnter = async (evt: OnStepEnterExitArgs<CountryIsoCode[]>) => {
    setHighlightedCountries(evt.data);
  };

  return (
    <>
      <CenteredStoryText>
        <Trans id="free-to-be-young.chapter-1.text-1">
          We asked the same question of people around the world.
        </Trans>
      </CenteredStoryText>

      <ScrollRoot>
        <ScrollGraphic>
          <StripPlot
            data={chartData}
            highlightedIds={highlightedCountries}
            axisLabel={t({
              id: 'free-to-be-young.chapter-1.chart-label',
              message:
                "% of 15–24 year-olds proposing a voting age below their country's current minimum",
            })}
            formatValue={formatValue}
            getValue={(d) => d.value}
          />
        </ScrollGraphic>

        <ScrollSteps onStepEnter={handleStepEnter}>
          <ScrollStep>
            <StoryText>
              <Trans id="free-to-be-young.chapter-1.text-2">
                In <PrimaryAnnotatedType>certain countries,</PrimaryAnnotatedType> a significant
                number of young people propose a voting age below their country's existing minimum.
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={['FR', 'DE', 'UK']}>
            <StoryText>
              <Trans id="free-to-be-young.chapter-1.text-3">
                A desire for a lower minimum voting age is especially common in{' '}
                <PrimaryAnnotatedType>high-income countries.</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={['CM', 'LB']}>
            <StoryText>
              <Trans id="free-to-be-young.chapter-1.text-4">
                It is also the case in <PrimaryAnnotatedType>Cameroon</PrimaryAnnotatedType> and{' '}
                <PrimaryAnnotatedType>Lebanon</PrimaryAnnotatedType> where the prevailing legal
                voting age is very high - 20 in{' '}
                <PrimaryAnnotatedType>Cameroon</PrimaryAnnotatedType> and 21 in{' '}
                <PrimaryAnnotatedType>Lebanon.</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep>
            <StoryText>
              <Trans id="free-to-be-young.chapter-1.text-5">
                Many older people also expressed support for a lower minimum voting age.
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>
    </>
  );
}
